/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';

import { admin } from 'apis/admin';
import useIsMobile from 'hooks/useIsMobile';
import { useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { userSelectorFamily } from 'recoil/login';
import { ADMIN, AGENT_TYPE } from 'shared/constants';
import { generateStorage } from 'shared/lib';
import { LoginStorage } from 'shared/lib';
import styled from 'styled-components';
import AppFooter from 'widgets/footer';
import { Header } from 'widgets/header';

import AppContent from '../components/Content';

const BACKGROUND_COLOR_MAP = {
  '/guide': 'Gray10',
  '/login': 'Gray10',
  '/admin/login': 'Gray10',
  '/register': 'Gray10',
  '/profile/edit': 'Gray10',
};

// 모바일 인증/진입·프로필 페이지는 디자인상 푸터 미노출
// 정산 온보딩 게이트 — 정책 전문(요율·계좌 포함)이 들어 있어 메인 청크에 싣지 않는다.
// 로그인한 회원에게만 의미가 있으므로 지연 로드(딥 임포트: 청크 무게 예외, 정책 페이지와 동일 이유).
// 선택적 UI라 청크 로드 실패 시 새로고침·에러 화면으로 번지지 않고 조용히 미노출한다(fail-open).
const SettlementOnboardingGate = React.lazy(() =>
  import('features/contract/ui/SettlementOnboardingGate')
    .then((m) => ({ default: m.SettlementOnboardingGate }))
    .catch(() => ({ default: () => null })),
);

const MOBILE_FOOTERLESS_PATHS = ['/login', '/admin/login', '/kakaologin', '/register', '/profile/edit'];

// 모바일에서 흰 배경을 쓰는 페이지(데스크톱은 BACKGROUND_COLOR_MAP 유지)
const MOBILE_WHITE_BG_PATHS = ['/profile/edit', '/register', '/login'];

const DefaultLayout = () => {
  const location = useLocation();
  const safeLoginStorage = generateStorage();
  const [, setIsAdmin] = useRecoilState(userSelectorFamily('isAdmin'));
  const [, setName] = useRecoilState(userSelectorFamily('name'));
  const [, setId] = useRecoilState(userSelectorFamily('id'));
  const [, setAgencyType] = useRecoilState(userSelectorFamily('agencyType'));
  const [, setMemberRole] = useRecoilState(userSelectorFamily('memberRole'));
  const isMobile = useIsMobile();
  const useMobileWhiteBg = isMobile && MOBILE_WHITE_BG_PATHS.includes(location.pathname);
  const backgroundColor = useMobileWhiteBg
    ? undefined
    : BACKGROUND_COLOR_MAP[location.pathname as keyof typeof BACKGROUND_COLOR_MAP];
  // 매물 등록(/room/create)·수정(/room/create/:id)도 모바일에선 푸터 미노출
  const hideFooter =
    isMobile && (MOBILE_FOOTERLESS_PATHS.includes(location.pathname) || location.pathname.startsWith('/room/create'));

  useEffect(() => {
    if (!LoginStorage.isLogin()) {
      safeLoginStorage.remove(ADMIN);
      safeLoginStorage.remove(AGENT_TYPE);
      LoginStorage.removeToken();
      return;
    }
    admin
      .info()
      .then((data) => {
        if (!data.isCheckmateAdmin && !data.firstName && !data.lastName) {
          alert('아직 회원가입을 완료하지 않았습니다. 회원가입을 진행해주세요.');
          window.location.href = '/register?email=' + data.email;
          return;
        }

        safeLoginStorage.set(ADMIN, data.isCheckmateAdmin ? 'true' : 'false');
        safeLoginStorage.set(AGENT_TYPE, data.agencyType ?? '');
        setIsAdmin(data.isCheckmateAdmin);
        setName(data.lastName + data.firstName);
        setId(data.id);
        setAgencyType(data.agencyType ?? '');
        setMemberRole(data.memberRole ?? '');
      })
      .catch(() => {
        safeLoginStorage.remove(ADMIN);
        safeLoginStorage.remove(AGENT_TYPE);
        LoginStorage.removeToken();
        window.location.href = '/login';
      });
  }, []);

  return (
    <Root backgroundColor={backgroundColor}>
      <Header />
      {/* 로그인 직후 정산 온보딩(정책 동의 게이트 · 5% 포함 셀프 확인) — 비로그인은 청크도 받지 않는다 */}
      {LoginStorage.isLogin() && (
        <React.Suspense fallback={null}>
          <SettlementOnboardingGate />
        </React.Suspense>
      )}
      <AppContent />
      {!hideFooter && <AppFooter />}
    </Root>
  );
};

const Root = styled.div<{
  backgroundColor?: string;
}>`
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    Roboto,
    'Helvetica Neue',
    'Segoe UI',
    'Apple SD Gothic Neo',
    'Noto Sans KR',
    'Malgun Gothic',
    'Apple Color Emoji',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
    sans-serif;
  display: flex;
  flex-direction: column;

  width: 100vw;
  min-height: 100vh;
  position: relative;
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor ? theme.color[backgroundColor] : theme.color.White};

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox  */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

export default DefaultLayout;
